import { useTranslation } from 'react-i18next';
import {
  COUNTRY_CODE_OPTIONS,
  GENDER_OPTIONS,
  MONTH_OPTIONS,
  NATIONALITY_OPTIONS,
  YEAR_OPTIONS,
} from 'shared/constants';

// TODO: 해당 로직 추후 개선한다.
// NOTE: 폼에서 사용하고 있는 함수들 파악

/**
 * 직업 옵션 생성을 위한 원본 i18n 키 목록.
 * 번역 훅에서 가져온 텍스트와 동일한 순서를 유지한다.
 */
const OCCUPATION_KEYS = [
  'undergraduateStudent',
  'graduateStudent',
  'exchangeStudent',
  'educationResearch',
  'itSoftwareEngineering',
  'manufactureAgriculture',
  'artsDesign',
  'financeBanking',
  'publicServiceGovernment',
  'healthcareMedical',
  'businessSales',
  'legalConsulting',
  'others',
];

/**
 * 비자 옵션 생성을 위한 원본 i18n 키 목록.
 * 각 항목은 고정된 비자 코드 문자열과 결합되어 표시된다.
 */
const VISA_INFO_KEYS = [
  'diplomaticOfficial',
  'visaExempted',
  'journalismReligious',
  'shortTermVisit',
  'study',
  'languageStudy',
  'technicalTrainee',
  'intraCompanyTransfer',
  'professional',
  'nonProfessional',
  'workingHoliday',
  'workAndVisit',
  'familyVisitor',
  'overseasKorean',
  'permanentResidence',
  'marriageMigrant',
  'others',
];

/** 성별 옵션 생성을 위한 i18n 키 목록 */
const GENDER_KEYS = ['male', 'female'];

/** 선호 언어 옵션 생성을 위한 i18n 키 목록 */
const PREFERRED_LANGUAGE_KEYS = ['simplifiedChinese', 'english', 'vietnamese', 'japanese'];
const PREFERRED_LANGUAGE_ENUMS = ['SIMPLIFIED_CHINESE', 'ENGLISH', 'VIETNAMESE', 'JAPANESE'];
/** 월 옵션 생성을 위한 i18n 키 목록 (영문 축약형 순서 유지) */
const MONTH_KEYS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/**
 * 다국어 폼 셀렉트 옵션과 원본 값 변환 헬퍼를 제공하는 훅.
 *
 * @remarks
 * - `react-i18next`의 `useTranslation`을 사용해 지역화된 문자열을 생성한다.
 * - 번역된 텍스트와 원본 코드/영문 값 간의 매핑이 필요한 폼 구성에 활용된다.
 *
 * @returns `Form`에서 활용 가능한 옵션 배열과 변환 헬퍼 객체
 */
export const useI18nOptions = () => {
  const { t } = useTranslation('common');

  const genderOptions = GENDER_KEYS.map((key) => t(`options.gender.${key}`));
  const monthOptions = MONTH_KEYS.map((key) => t(`options.months.${key}`));
  const preferredLanguageOptions = PREFERRED_LANGUAGE_KEYS.map((key) => t(`options.preferredLanguage.${key}`));

  /**
   * 로컬라이즈된 직업 옵션 배열.
   * 원본 `OCCUPATION_KEYS` 순서와 매핑되어 원본 값 변환이 가능하다.
   */
  const occupationOptions = OCCUPATION_KEYS.map((key) => t(`options.occupation.${key}`));

  /**
   * 월과 연도 문자열을 기반으로 일(day) 옵션 배열을 반환한다.
   *
   * @param month 로컬라이즈된 월 문자열
   * @param year 4자리 연도 문자열
   * @returns 월별 일수에 맞는 2자리 문자열 배열
   */
  const getDayOptionsByMonthAndYear = (month: string, year: string) => {
    const yearNum = parseInt(year) ?? 2000;

    // Find the month index from localized month values
    const monthIndex = monthOptions.findIndex((option) => option === month);
    if (monthIndex === -1) return [];

    // Use the English month abbreviation for the logic (using the same order as MONTH_KEYS)
    const englishMonth = MONTH_OPTIONS[monthIndex];

    switch (englishMonth) {
      case 'JAN':
      case 'MAR':
      case 'MAY':
      case 'JUL':
      case 'AUG':
      case 'OCT':
      case 'DEC':
        return Array.from({ length: 31 }, (_, i) => (i < 9 ? `0${i + 1}` : (i + 1).toString()));
      case 'APR':
      case 'JUN':
      case 'SEP':
      case 'NOV':
        return Array.from({ length: 30 }, (_, i) => (i < 9 ? `0${i + 1}` : (i + 1).toString()));
      case 'FEB':
        if ((yearNum % 4 === 0 && yearNum % 100 !== 0) || yearNum % 400 === 0) {
          return Array.from({ length: 29 }, (_, i) => (i < 9 ? `0${i + 1}` : (i + 1).toString()));
        }
        return Array.from({ length: 28 }, (_, i) => (i < 9 ? `0${i + 1}` : (i + 1).toString()));
      default:
        return [];
    }
  };

  /**
   * 로컬라이즈된 비자 옵션 배열.
   * 번역된 텍스트와 고정된 비자 코드를 조합하여 반환한다.
   */
  const visaInfoOptions = VISA_INFO_KEYS.map((key, index) => {
    const baseText = t(`options.visaInfo.${key}`);
    const codes = [
      '(A-1,A-2)',
      '(B)',
      '(C-1, D-5, D-6)',
      '(C-2, C-3)',
      '(D-2)',
      '(D-4)',
      '(D-3)',
      '(D-7)',
      '(C-4, D-1, D-8, D-9, D-10, E-1, E-2, E-3, E-4, E-5, E-6, E-7)',
      '(E-8, E-9, E-10)',
      '(H-1)',
      '(H-2)',
      '(F-1, F-2, F-3)',
      '(F-4)',
      '(F-5)',
      '(F-6)',
      '(G-1)',
    ];
    return baseText + codes[index];
  });

  /**
   * 인덱스를 기반으로 원본 성별 코드를 반환한다.
   *
   * @param index 성별 옵션 인덱스
   * @returns 영문 성별 코드 (기본값: `MALE`)
   */
  const getOriginalGenderByIndex = (index: number): string => GENDER_OPTIONS[index] || 'MALE';
  /**
   * 인덱스를 기반으로 원본 월(영문 축약형)을 반환한다.
   *
   * @param index 월 옵션 인덱스
   * @returns 영문 월 축약형 (기본값: `JAN`)
   */
  const getOriginalMonthByIndex = (index: number): string => MONTH_OPTIONS[index] || 'JAN';

  /**
   * 로컬라이즈된 월 문자열을 영문 축약형으로 변환한다.
   *
   * @param localizedMonth 로컬라이즈된 월 문자열
   * @returns 영문 월 축약형 또는 일치하는 값이 없는 경우 입력값
   */
  const getEnglishMonth = (localizedMonth: string): string => {
    const monthIndex = monthOptions.findIndex((option) => option === localizedMonth);
    return monthIndex !== -1 ? MONTH_OPTIONS[monthIndex] : localizedMonth;
  };

  /**
   * 인덱스를 기반으로 원본 직업명을 반환한다.
   *
   * @param index 직업 옵션 인덱스
   * @returns 영문 직업 문자열 (기본값: `Others`)
   */
  const getOriginalOccupationByIndex = (index: number): string => {
    const originalOccupations = [
      'Undergraduate Student',
      'Graduate Student',
      'Exchange Student',
      'Education/Academic Research',
      'IT/Software/Engineering',
      'Manufacture/Agriculture',
      'Arts/Design',
      'Finance/Banking',
      'Public Service/Government',
      'Healthcare/Medical Service',
      'Business/Sales',
      'Legal/Consulting',
      'Others',
    ];
    return originalOccupations[index] || 'Others';
  };

  /**
   * 인덱스를 기반으로 원본 비자 정보 문자열을 반환한다.
   *
   * @param index 비자 옵션 인덱스
   * @returns 영문 비자 정보 문자열 (기본값: `Others(G-1)`)
   */
  const getOriginalPreferredLanguageByIndex = (index: number): string => PREFERRED_LANGUAGE_ENUMS[index] || 'ENGLISH';

  const getOriginalVisaInfoByIndex = (index: number): string => {
    const originalVisaInfos = [
      'Diplomatic/Official(A-1,A-2)',
      'Visa Exempted(B)',
      'Journalism/Religious Affairs(C-1, D-5, D-6)',
      'Short Term Visit(C-2, C-3)',
      'Study(D-2)',
      'Language Study(D-4)',
      'Technical Trainee(D-3)',
      'Intra-Company Transfer(D-7)',
      'Professional(C-4, D-1, D-8, D-9, D-10, E-1, E-2, E-3, E-4, E-5, E-6, E-7)',
      'Non-Professional(E-8, E-9, E-10)',
      'Working Holiday(H-1)',
      'Work and Visit(H-2)',
      'Family Visitor/Dependent Family(F-1, F-2, F-3)',
      'Overseas Korean(F-4)',
      'Permanent Residence(F-5)',
      'Marriage Migrant(F-6)',
      'Others(G-1)',
    ];
    return originalVisaInfos[index] || 'Others(G-1)';
  };

  /**
   * 폼 컴포넌트에서 사용할 옵션 및 헬퍼 모음.
   */
  return {
    genderOptions,
    monthOptions,
    occupationOptions,
    visaInfoOptions,
    preferredLanguageOptions,
    getDayOptionsByMonthAndYear,
    // Helper functions for converting indices to original English values
    getOriginalGenderByIndex,
    getOriginalMonthByIndex,
    getOriginalOccupationByIndex,
    getOriginalVisaInfoByIndex,
    getOriginalPreferredLanguageByIndex,
    getEnglishMonth,
    // Keep these as they are (unchanged)
    countryCodeOptions: COUNTRY_CODE_OPTIONS,
    nationalityOptions: NATIONALITY_OPTIONS,
    yearOptions: YEAR_OPTIONS,
  };
};
